import { Box, Button, LinearProgress } from "@mui/material";
import { UploadIcon } from "../../Icons/Icons";
import { useEffect, useState } from "react";

interface FileUploadProps {
  isFileUploading: boolean;
  selectedFile: any;
  showCancel?: boolean;
  onFileChange: (file: any) => void;
  onCancelUpload: () => void;
  handleFileSubmission?: (event: any) => void;
  modelType: string;
  allowMultiple?: boolean; // New prop to control multiple file selection
}

const FileUpload = (props: FileUploadProps) => {
  const {
    isFileUploading,
    selectedFile,
    showCancel = true,
    onFileChange,
    onCancelUpload,
    handleFileSubmission,
    modelType,
    allowMultiple,
  } = props;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const stopDefaults = (event: React.DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const dragEvents = {
    onDragOver: stopDefaults,
    onDrop: (event: React.DragEvent<HTMLElement>) => {
      stopDefaults(event);
      if (event.dataTransfer.files.length > 0) {
        // Use allowMultiple prop to determine if multiple files are allowed
        if (allowMultiple) {
          onFileChange(Array.from(event.dataTransfer.files));
        } else {
          onFileChange(event.dataTransfer.files[0]);
        }
      }
    },
  };

  const handleChange = (event: any) => {
    if (allowMultiple) {
      // Allow multiple files
      onFileChange(Array.from(event?.target?.files || []));
    } else {
      // Take first file only
      onFileChange(event?.target?.files[0]);
    }
  };

  const getFileDisplayText = () => {
    if (!selectedFile) return "";
    
    if (Array.isArray(selectedFile)) {
      if (selectedFile.length === 1) {
        return selectedFile[0].name;
      } else {
        return `${selectedFile.length} files selected`;
      }
    }
    
    return selectedFile.name;
  };

  const uploader = (
    <>
      <input
        onChange={handleChange}
        accept="image/png, image/jpeg"
        className="file-upload"
        id="fileUpload"
        type="file"
        capture={undefined}
        multiple={allowMultiple}
      />

      {isMobile ? (
        <div className="mobile-upload-buttons">
          <input
            onChange={handleChange}
            accept="image/png, image/jpeg"
            className="file-upload"
            id="takePhoto"
            type="file"
            capture="environment"
            multiple={allowMultiple}
          />
          <input
            onChange={handleChange}
            accept="image/png, image/jpeg"
            className="file-upload"
            id="uploadPhoto"
            type="file"
            multiple={allowMultiple}
          />
          <label htmlFor="takePhoto" className="mobile-button">
            <Box>
              <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
              <p>Take a photo</p>
            </Box>
          </label>
          <label htmlFor="uploadPhoto" className="mobile-button">
            <Box>
              <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
              <p>Upload {allowMultiple ? "photos" : "a photo"}</p>
            </Box>
          </label>
        </div>
      ) : (
        <label htmlFor="fileUpload" {...dragEvents}>
          <Box>
            <img src={`/images/FileUpload_${modelType}.png`} alt={`${modelType}.png`} />
            <p>Drag & drop {allowMultiple ? "images" : "an image"}</p>
            <span>Supported formats: JPG, PNG. </span>
            {allowMultiple && <span><br/>You can select multiple files.</span>}
          </Box>
        </label>
      )}
    </>
  );

  const loader = (
    <div className="loader-wrapper">
      <h3>Uploading</h3>
      <div className="loader">
        <p>{getFileDisplayText()}</p>
        <Box sx={{ width: "100%" }}>
          <LinearProgress
            classes={{
              colorPrimary: "progress-bg",
              bar: "progress-bar",
            }}
          />
        </Box>
      </div>
      <Button
        variant="outlined"
        className="btn btn-outlined"
        onClick={onCancelUpload}
      >
        Cancel
      </Button>
    </div>
  );

  const controls = (
    <>
      <div className="row">
        <Button
          variant="contained"
          className="btn btn-blue"
          disabled={!selectedFile || (Array.isArray(selectedFile) && selectedFile.length === 0)}
          onClick={handleFileSubmission}
          title="Click to upload the selected image(s)"
        >
          <UploadIcon
            color={!selectedFile || (Array.isArray(selectedFile) && selectedFile.length === 0) ? "rgba(0, 0, 0, 0.12)" : "#fff"}
          />
          Upload {allowMultiple && Array.isArray(selectedFile) && selectedFile.length > 1 ? "Images" : "Image"}
        </Button>
      </div>
      {showCancel && (
        <div className="row">
          <Button
            variant="outlined"
            className="btn btn-outlined"
            onClick={onCancelUpload}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div className="file-upload-wrapper">
      <div className="row">{uploader}</div>
      {!isFileUploading && <div className="row">{controls}</div>}
      {isFileUploading && <div className="row">{loader}</div>}
    </div>
  );
};

export default FileUpload;
